// ── Store composite doc ids ───────────────────────────────────────────────────
// Refs imported from a store catalogue key their Firestore doc id off the
// store's own immutable product ids instead of the ref string. For Shopify that
// decouples identity from a SKU reused across products (e.g. Alkin's
// strap-position `AM1-B-1`); for Square Online, whose products rarely carry a
// model number at all, it keeps a name-as-ref from being the identity. `__`
// separates the segments for readability. Pure — no Firebase — so the importer,
// the scrapers and their tests can all share it.

/** Variant / standalone doc id: `shopify__{productId}__{variantId}`. */
export function shopifyVariantDocId(productId: string, variantId: string): string {
  return `shopify__${productId}__${variantId}`;
}

/** Parent doc id (one per multi-variant Shopify product): `shopify__{productId}__parent`. */
export function shopifyParentDocId(productId: string): string {
  return `shopify__${productId}__parent`;
}

/** Square Online product doc id: `square__{siteId}__{productId}`. */
export function squareProductDocId(siteId: string, productId: string): string {
  return `square__${siteId}__${productId}`;
}

/**
 * WooCommerce product doc id: `woocommerce__{host}__{productId}`. Product ids
 * are only unique within one WordPress site, so the shop's host is part of the
 * key — pass it through {@link wooHost} so `www.` and case never fork a doc.
 */
export function woocommerceProductDocId(host: string, productId: string | number): string {
  return `woocommerce__${host}__${productId}`;
}

/** A shop's canonical host for doc ids: lowercased, leading `www.` dropped. */
export function wooHost(url: string): string {
  return new URL(url).hostname.toLowerCase().replace(/^www\./, '');
}

/** The store ids an import entry may carry. */
export interface StoreIdentifiedEntry {
  shopifyProductId?: string;
  shopifyVariantId?: string;
  shopifyVariantCount?: number;
  squareSiteId?: string;
  squareProductId?: string;
  /** Set only when a multi-option Square product is expanded into variants. */
  squareSkuId?: string;
  wooHost?: string;
  wooProductId?: string;
  /** Set only when a variable WooCommerce product is expanded into variants. */
  wooVariationId?: string;
  /** How many variants the expanded store product has (WooCommerce / Square). */
  storeVariantCount?: number;
}

/**
 * The composite doc id for an entry imported from a store catalogue, or
 * undefined for entries that keep ref-string identity (file, URL, manual).
 */
export function storeDocIdForEntry(entry: StoreIdentifiedEntry): string | undefined {
  if (entry.shopifyProductId && entry.shopifyVariantId) {
    return shopifyVariantDocId(entry.shopifyProductId, entry.shopifyVariantId);
  }
  if (entry.squareSiteId && entry.squareProductId) {
    const product = squareProductDocId(entry.squareSiteId, entry.squareProductId);
    return entry.squareSkuId ? `${product}__${entry.squareSkuId}` : product;
  }
  if (entry.wooHost && entry.wooProductId) {
    const product = woocommerceProductDocId(entry.wooHost, entry.wooProductId);
    return entry.wooVariationId ? `${product}__${entry.wooVariationId}` : product;
  }
  return undefined;
}

/**
 * The parent/variant group an import entry belongs to, or null for a
 * standalone reference. One shape for every store, so the importer's parent
 * pre-pass, routing and AI-enrich skip treat Shopify, WooCommerce and Square
 * Online variants the same way.
 *
 *  - Shopify: grouped by product id (the key stays the bare product id, as it
 *    always was); parent doc `shopify__{productId}__parent`.
 *  - WooCommerce / Square Online (Phase 2 variant expansion): grouped by the
 *    product, and the parent doc IS the product-level doc id the v1 importer
 *    wrote (`woocommerce__{host}__{productId}`, `square__{siteId}__{productId}`)
 *    — so a product imported before expansion existed becomes the parent on
 *    re-import, instead of being left behind as a duplicate.
 *
 * A product needs 2+ variants to be a group; a single-variant product imports
 * standalone, as on Shopify.
 */
export interface VariantGroup {
  key: string;
  parentDocId: string;
  source: 'shopify' | 'woocommerce' | 'square_online';
  /** The store's product id (Shopify: for `shopifyProductId` on the parent). */
  productId: string;
}

export function variantGroupOf(entry: StoreIdentifiedEntry): VariantGroup | null {
  if (entry.shopifyProductId) {
    if ((entry.shopifyVariantCount ?? 0) < 2) return null;
    return {
      key: entry.shopifyProductId,
      parentDocId: shopifyParentDocId(entry.shopifyProductId),
      source: 'shopify',
      productId: entry.shopifyProductId,
    };
  }
  if ((entry.storeVariantCount ?? 0) < 2) return null;
  if (entry.wooHost && entry.wooProductId && entry.wooVariationId) {
    const parentDocId = woocommerceProductDocId(entry.wooHost, entry.wooProductId);
    return { key: parentDocId, parentDocId, source: 'woocommerce', productId: entry.wooProductId };
  }
  if (entry.squareSiteId && entry.squareProductId && entry.squareSkuId) {
    const parentDocId = squareProductDocId(entry.squareSiteId, entry.squareProductId);
    return { key: parentDocId, parentDocId, source: 'square_online', productId: entry.squareProductId };
  }
  return null;
}
